import { TBaseButtonProps } from './BaseButton.types';
import { withLoaderOnPress } from '../../../hoc/withLoaderOnPress';
import { Pressable, View } from 'react-native';
import { useFocus } from 'src/hooks/useFocus';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { forwardRef, useRef, useState } from 'react';
import { DEFAULT_FOCUS_RING } from 'src/theme-v2/focus';

const BaseButtonComponent = forwardRef(
  (
    {
      children,
      focusId,
      focusOrder,
      focusGroup,
      focusable = true,
      innerStyle,
      style,
      disabledStyle,
      focusStyle,
      hoverStyle,
      pressedStyle,
      onHoverIn,
      onHoverOut,
      onPressIn,
      onPressOut,
      onKeyDown,
      onPress,
      disabled,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      accessibilityRole = 'button',
      accessibilityLabel,
      accessible = true,
      ...props
    }: TBaseButtonProps,
    ref,
  ) => {
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);

    const isFocusable = !disabled && focusable;

    const buttonRef = ref ?? useRef(null);
    const { blur, focused } = useFocus({
      id: focusId,
      order: focusOrder,
      group: focusGroup,
      focusable: isFocusable,
      ref: buttonRef,
    });

    const isActuallyFocused = focused;

    let buttonStyle;
    if (disabled) {
      buttonStyle = disabledStyle;
    } else if (pressed) {
      buttonStyle = pressedStyle;
    } else if (hovered) {
      buttonStyle = hoverStyle;
    } else if (isActuallyFocused) {
      const flatFocusStyle = Array.isArray(focusStyle) ? Object.assign({}, ...focusStyle.filter(Boolean)) : focusStyle;
      const hasFocusIndicator =
        flatFocusStyle != null &&
        ('borderColor' in flatFocusStyle || 'backgroundColor' in flatFocusStyle || 'boxShadow' in flatFocusStyle);
      buttonStyle = mergeStyles(hasFocusIndicator ? undefined : DEFAULT_FOCUS_RING, focusStyle);
    }

    return (
      <Pressable
        accessibilityState={{ disabled: !!disabled }}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel}
        accessible={accessible && typeof accessibilityLabel === 'string'}
        tabIndex={isFocusable ? 0 : -1}
        ref={buttonRef}
        onHoverIn={(...args) => {
          blur();
          setHovered(true);
          if (onHoverIn) onHoverIn(...args);
        }}
        onHoverOut={(...args) => {
          setHovered(false);
          setPressed(false);
          if (onHoverOut) onHoverOut(...args);
        }}
        onPressIn={(...args) => {
          if (disabled) return;
          setPressed(true);
          if (onPressIn) onPressIn(...args);
        }}
        onPressOut={(...args) => {
          if (disabled) return;
          setPressed(false);
          if (onPressOut) onPressOut(...args);
        }}
        onPress={(...args) => {
          if (disabled) return;
          setPressed(false);
          if (onPress) onPress(...args);
        }}
        onFocus={(e: any) => {
          if (onFocusProp) onFocusProp(e);
        }}
        onBlur={(e: any) => {
          if (onBlurProp) onBlurProp(e);
        }}
        style={mergeStyles(style, buttonStyle)}
        onKeyDown={(e: any) => {
          if (onKeyDown) onKeyDown(e);
        }}
        {...props}
      >
        <View
          onMouseDown={() => {
            setPressed(true);
          }}
          onMouseUp={() => {
            setPressed(false);
          }}
          style={mergeStyles(innerStyle)}
        >
          {children({ focused: isActuallyFocused, disabled: !!disabled, pressed, hovered })}
        </View>
      </Pressable>
    );
  },
);

export const BaseButton = withLoaderOnPress(BaseButtonComponent);
